import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, AlertTriangle } from "lucide-react";
import {
  fetchSubscriptionPanel,
  stripeCancelSubscription,
  stripeRefundLatest,
  stripeExtendSubscription,
  grantComplimentaryPro,
  extendComplimentaryPro,
  revokeComplimentaryPro,
  setAdminFullHistory,
  clearStoreId,
  type SubscriptionPanel as PanelData,
} from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { formatDay, formatPence, kindLabel, sourceLabel } from "./subscription-ui";

// Subscription panel inside the admin user drawer (spec 24 / ATL-104):
// where Pro comes from, what the provider says, the safe actions, and the
// billing_events history. One action form open at a time; every action
// needs a reason and a confirm, destructive ones name the user's email.

type Action =
  | "cancel_end"
  | "cancel_now"
  | "refund"
  | "stripe_extend"
  | "grant"
  | "extend"
  | "revoke"
  | "clear_apple"
  | "clear_google"
  | "fh_grant"
  | "fh_revoke";

const ACTION_LABEL: Record<Action, string> = {
  cancel_end: "Cancel at period end",
  cancel_now: "Cancel now",
  refund: "Refund latest invoice",
  stripe_extend: "Extend (free days)",
  grant: "Grant complimentary Pro",
  extend: "Extend",
  revoke: "Revoke",
  clear_apple: "Clear stale App Store id",
  clear_google: "Clear stale Google Play id",
  fh_grant: "Grant Full history",
  fh_revoke: "Revoke Full history",
};

const FULL_HISTORY_SOURCE: Record<string, string> = {
  stripe: "web purchase",
  apple: "App Store purchase",
  google: "Google Play purchase",
  annual: "annual Pro",
  supporter: "supporters bundle",
  admin: "complimentary",
  partner: "partner link",
};

const DESTRUCTIVE: ReadonlySet<Action> = new Set<Action>(["cancel_now", "refund", "revoke", "clear_apple", "clear_google", "fh_revoke"]);

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 32,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

function statusChip(p: PanelData) {
  const e = p.entitlement;
  if (!e.isPro) return <Badge variant="neutral">{e.plan === "pro" ? "Lapsed" : "Free"}</Badge>;
  if (e.billingStatus === "past_due") return <Badge variant="danger">Past due</Badge>;
  if (p.stripe?.live?.cancelAtPeriodEnd) {
    return <Badge variant="neutral">Cancels {formatDay(p.stripe.live.cancelAt ?? p.stripe.live.currentPeriodEnd)}</Badge>;
  }
  const latest = p.events[0];
  if (latest?.kind === "grace") return <Badge variant="danger">Grace period</Badge>;
  if (e.billingStatus === "lifetime") return <Badge variant="gold">Lifetime</Badge>;
  return <Badge variant="success">Active</Badge>;
}

export function SubscriptionPanel({ userId, email }: { userId: string; email: string }) {
  const queryClient = useQueryClient();
  const panelQuery = useQuery({
    queryKey: ["admin/subscriptions/users", userId],
    queryFn: () => fetchSubscriptionPanel(userId),
  });
  const [action, setAction] = React.useState<Action | null>(null);
  const [reason, setReason] = React.useState("");
  const [days, setDays] = React.useState("30");
  const [until, setUntil] = React.useState("");
  const [mode, setMode] = React.useState<"days" | "until">("days");
  const [includeFullHistory, setIncludeFullHistory] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);

  const done = (text: string) => {
    setMessage(text);
    setAction(null);
    setReason("");
    void queryClient.invalidateQueries({ queryKey: ["admin/subscriptions/users", userId] });
    void queryClient.invalidateQueries({ queryKey: ["admin/users"] });
    void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
  };

  const mutation = useMutation({
    mutationFn: async (a: Action) => {
      const r = reason.trim();
      const n = parseInt(days, 10);
      switch (a) {
        case "cancel_end":
          return stripeCancelSubscription(userId, { atPeriodEnd: true, reason: r }).then((res) => `Cancels ${formatDay(res.periodEnd)}.`);
        case "cancel_now":
          return stripeCancelSubscription(userId, { atPeriodEnd: false, reason: r }).then(() => "Subscription cancelled.");
        case "refund":
          return stripeRefundLatest(userId, { reason: r }).then((res) => `Refunded ${formatPence(res.amountPence, res.currency)}.`);
        case "stripe_extend":
          return stripeExtendSubscription(userId, { days: n, reason: r }).then((res) => `Next charge ${formatDay(res.periodEnd)}.`);
        case "grant":
          return grantComplimentaryPro(
            userId,
            mode === "until" ? { until, reason: r, includeFullHistory } : { days: n, reason: r, includeFullHistory },
          ).then((res) => `Pro until ${formatDay(res.expiresAt)}${res.fullHistory ? ", with Full history" : ""}.`);
        case "fh_grant":
          return setAdminFullHistory(userId, { grant: true, reason: r }).then(() => "Full history granted.");
        case "fh_revoke":
          return setAdminFullHistory(userId, { grant: false, reason: r }).then((res) =>
            res.fullHistory.unlocked
              ? `Complimentary grant removed. Still unlocked through ${FULL_HISTORY_SOURCE[res.fullHistory.source ?? ""] ?? "another source"}.`
              : "Full history revoked. Nothing collected was removed.",
          );
        case "extend":
          return extendComplimentaryPro(userId, { days: n, reason: r }).then((res) => `Pro until ${formatDay(res.expiresAt)}.`);
        case "revoke":
          return revokeComplimentaryPro(userId, { reason: r }).then(() => "Pro revoked. Nothing collected was removed.");
        case "clear_apple":
          return clearStoreId(userId, { provider: "apple", reason: r }).then(() => "App Store id cleared.");
        case "clear_google":
          return clearStoreId(userId, { provider: "google", reason: r }).then(() => "Google Play id cleared.");
      }
    },
    onSuccess: done,
  });

  const p = panelQuery.data;
  if (panelQuery.isLoading || !p) {
    return (
      <div>
        <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Subscription</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Skel height={18} />
          <Skel height={60} />
        </div>
      </div>
    );
  }

  const e = p.entitlement;
  const source = e.isPro ? e.planSource : "none";
  const timed = source === "key" || source === "invite" || source === "admin";
  const stripeLive = p.stripe?.live ?? null;
  const isStripe = source === "stripe" && !!p.stripe?.subscriptionId;
  const isStore = source === "apple" || source === "google";

  const available: Action[] = [];
  if (isStripe) {
    if (!stripeLive?.cancelAtPeriodEnd) available.push("cancel_end");
    available.push("cancel_now", "refund", "stripe_extend");
  } else if (timed) {
    available.push("extend", "revoke");
  } else if (!e.isPro || source === "none") {
    available.push("grant");
  }
  if (p.apple && (!p.apple.live || !p.apple.live.active) && source !== "apple") available.push("clear_apple");
  if (p.google && (!p.google.live || !p.google.live.active) && source !== "google") available.push("clear_google");
  // Spec 30: Full history sits beside Pro. Revoke only takes back the
  // complimentary (admin) source; a purchase or annual grant stays.
  if (e.fullHistory.unlocked) available.push("fh_revoke");
  else available.push("fh_grant");

  const needsDays = action === "stripe_extend" || action === "extend" || (action === "grant" && mode === "days");
  const daysNum = parseInt(days, 10);
  const daysOk = !needsDays || (Number.isInteger(daysNum) && daysNum >= 1 && daysNum <= (action === "stripe_extend" ? 90 : 3650));
  const untilOk = action !== "grant" || mode !== "until" || (until !== "" && new Date(until) > new Date());
  const canSubmit = !!action && reason.trim().length > 0 && daysOk && untilOk && !mutation.isPending;

  const factCell = (label: string, value: React.ReactNode) => (
    <React.Fragment key={label}>
      <div style={{ color: "var(--text-muted)" }}>{label}</div>
      <div style={{ color: "var(--text-primary)", textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }} className="tnum">{value}</div>
    </React.Fragment>
  );

  const facts: React.ReactNode[] = [];
  if (stripeLive) {
    facts.push(
      factCell("Price", stripeLive.amountPence != null ? `${formatPence(stripeLive.amountPence, stripeLive.currency)} / ${stripeLive.interval ?? "period"}` : "—"),
      factCell(stripeLive.cancelAtPeriodEnd ? "Ends" : "Renews", formatDay(stripeLive.cancelAt ?? stripeLive.currentPeriodEnd)),
      factCell("Stripe status", stripeLive.status),
    );
    if (stripeLive.trialEnd) facts.push(factCell("Free until", formatDay(stripeLive.trialEnd)));
    if (stripeLive.latestInvoice) {
      facts.push(
        factCell(
          "Latest invoice",
          <span>
            {formatPence(stripeLive.latestInvoice.amountPaid, stripeLive.currency)} · {stripeLive.latestInvoice.status ?? "—"}
            {stripeLive.latestInvoice.hostedUrl && (
              <a href={stripeLive.latestInvoice.hostedUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: "var(--sky-300)" }}>
                <ExternalLink size={11} />
              </a>
            )}
          </span>,
        ),
      );
    }
  }
  if (p.stripe) facts.push(factCell("Payment failures (30 d)", p.stripe.paymentFailures30d));
  if (e.planExpiresAt) facts.push(factCell(e.isPro ? "Pro until" : "Expired", formatDay(e.planExpiresAt)));
  facts.push(
    factCell(
      "Full history",
      e.fullHistory.unlocked
        ? `${FULL_HISTORY_SOURCE[e.fullHistory.source ?? ""] ?? "unlocked"} · since ${formatDay(e.fullHistory.since)}`
        : "Not unlocked",
    ),
  );
  facts.push(factCell("History cutoff", e.historyCutoff ? formatDay(e.historyCutoff) : "None (full archive)"));
  if (p.apple) {
    facts.push(
      factCell("App Store", p.apple.live ? `${p.apple.live.active ? "active" : "inactive"} · ${p.apple.live.environment}${p.apple.live.expiresAt ? ` · to ${formatDay(p.apple.live.expiresAt)}` : ""}` : p.apple.configured ? "lookup failed" : "not configured"),
      factCell("Original transaction", <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{p.apple.originalTransactionId}</span>),
    );
  }
  if (p.google) {
    facts.push(
      factCell("Google Play", p.google.live ? `${p.google.live.active ? "active" : "inactive"}${p.google.live.expiresAt ? ` · to ${formatDay(p.google.live.expiresAt)}` : ""}` : p.google.configured ? "lookup failed" : "not configured"),
      factCell("Purchase token", <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{p.google.purchaseToken}</span>),
    );
  }
  if (p.key) facts.push(factCell("Plan key", <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{p.key.code}</span>));
  if (p.invite) facts.push(factCell("Beta invite", `${p.invite.label ?? p.invite.code}${p.invite.removedAt ? " (removed)" : ""}`));

  return (
    <div>
      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 8 }}>Subscription</p>

      {/* Header line */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 10 }}>
        <Badge variant={e.isPro ? "gold" : "neutral"}>{e.isPro ? "Pro" : "Free"}</Badge>
        <Badge variant="info">{sourceLabel(source)}</Badge>
        {statusChip(p)}
      </div>

      {/* Conflicts */}
      {p.conflicts.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "8px 10px",
            marginBottom: 10,
            background: "var(--danger-soft)",
            border: "1px solid rgba(224, 80, 63, 0.4)",
            borderRadius: "var(--radius-md)",
            fontSize: 12,
            color: "var(--text-secondary)",
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2, color: "var(--status-danger)" }} />
          <div>
            {p.conflicts.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </div>
        </div>
      )}

      {/* Facts */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 14px", fontSize: 12 }}>
        {facts}
      </div>

      {/* Links */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, fontSize: 12 }}>
        {p.stripe?.dashboardUrl && (
          <a href={p.stripe.dashboardUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--sky-300)", textDecoration: "none" }}>
            <ExternalLink size={12} /> Stripe customer
          </a>
        )}
        {p.apple && (
          <a href={p.apple.consoleUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--sky-300)", textDecoration: "none" }}>
            <ExternalLink size={12} /> App Store Connect
          </a>
        )}
        {p.google && (
          <a href={p.google.consoleUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--sky-300)", textDecoration: "none" }}>
            <ExternalLink size={12} /> Play Console
          </a>
        )}
      </div>

      {isStore && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "10px 0 0", lineHeight: 1.5 }}>
          Refunds and cancellations for store subscriptions are handled by the store. Search the console
          for the id above; Atlas mirrors whatever the store decides.
        </p>
      )}

      {/* Actions */}
      {available.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {action === null ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {available.map((a, i) => (
                <Button
                  key={a}
                  size="sm"
                  variant={i === 0 && !DESTRUCTIVE.has(a) ? "primary" : "secondary"}
                  onClick={() => {
                    setAction(a);
                    setMessage(null);
                    mutation.reset();
                  }}
                >
                  {ACTION_LABEL[a]}
                </Button>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 10, background: "var(--surface-sunken)", borderRadius: "var(--radius-md)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{ACTION_LABEL[action]}</div>
              {action === "grant" && (
                <div style={{ display: "flex", gap: 6 }}>
                  <Button size="sm" variant={mode === "days" ? "secondary" : "ghost"} onClick={() => setMode("days")}>For N days</Button>
                  <Button size="sm" variant={mode === "until" ? "secondary" : "ghost"} onClick={() => setMode("until")}>Until a date</Button>
                </div>
              )}
              {needsDays && (
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Days {action === "stripe_extend" ? "(1 to 90)" : "(1 to 3650)"}
                  <input type="number" min={1} max={action === "stripe_extend" ? 90 : 3650} value={days} onChange={(ev) => setDays(ev.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
                </label>
              )}
              {action === "grant" && !e.fullHistory.unlocked && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-secondary)" }}>
                  <input
                    type="checkbox"
                    checked={includeFullHistory}
                    onChange={(ev) => setIncludeFullHistory(ev.target.checked)}
                  />
                  Include Full history (permanent, lifts the 12-month window)
                </label>
              )}
              {action === "grant" && mode === "until" && (
                <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Until
                  <input type="date" value={until} onChange={(ev) => setUntil(ev.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
                </label>
              )}
              <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                Reason (required, goes in the audit log)
                <input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Why this change is being made" style={{ ...inputStyle, marginTop: 4 }} maxLength={500} />
              </label>
              {action === "stripe_extend" && (
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
                  Uses Stripe trial_end, so the customer portal and receipt will show a trial for the free stretch.
                </p>
              )}
              {action === "refund" && (
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
                  Full refund of the latest paid invoice from the last 90 days. The plan is not changed.
                </p>
              )}
              {action === "revoke" && (
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
                  Ends the entitlement only. Nothing they have collected is deleted.
                </p>
              )}
              {action === "fh_grant" && (
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
                  Permanent unlock (source complimentary). Older activities start matching straight away.
                </p>
              )}
              {action === "fh_revoke" && (
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
                  Takes back the complimentary grant only. If a purchase, annual Pro or another grant still
                  covers it, Full history stays on. Nothing they have collected is deleted.
                </p>
              )}
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <Button size="sm" variant="ghost" onClick={() => setAction(null)}>Cancel</Button>
                <Button
                  size="sm"
                  variant={DESTRUCTIVE.has(action) ? "danger" : "primary"}
                  disabled={!canSubmit}
                  onClick={() => mutation.mutate(action)}
                >
                  {mutation.isPending ? "Working…" : DESTRUCTIVE.has(action) ? `Confirm for ${email}` : "Confirm"}
                </Button>
              </div>
              {mutation.isError && (
                <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
                  {errorMessage(mutation.error, "Action failed")}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {message && <p style={{ fontSize: 12, color: "var(--spruce-300)", margin: "8px 0 0" }}>{message}</p>}

      {/* Event history */}
      <div style={{ marginTop: 14 }}>
        <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Billing history</p>
        {p.events.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No billing events yet.</p>
        ) : (
          <div className="r-scroll-x" style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "4px 10px", fontSize: 12, minWidth: 320 }}>
              {p.events.map((ev) => (
                <React.Fragment key={ev.id}>
                  <div className="tnum" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }} title={ev.occurredAt}>{formatDay(ev.occurredAt)}</div>
                  <div style={{ color: "var(--text-primary)", minWidth: 0 }}>
                    <span style={{ color: "var(--text-muted)" }}>{sourceLabel(ev.provider)} · </span>
                    {kindLabel(ev.kind)}
                    {ev.periodEnd && (ev.kind === "cancel_scheduled" || ev.kind.startsWith("admin_") || ev.kind === "payment_failed" || ev.kind === "invite_claimed" || ev.kind === "key_redeemed") && (
                      <span style={{ color: "var(--text-muted)" }}> · {ev.kind === "payment_failed" ? "retry" : "to"} {formatDay(ev.periodEnd)}</span>
                    )}
                    {ev.reason && <div style={{ color: "var(--text-secondary)", fontSize: 11 }}>{ev.adminEmail ? `${ev.adminEmail}: ` : ""}{ev.reason}</div>}
                  </div>
                  <div className="tnum" style={{ textAlign: "right", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                    {ev.amountPence != null ? formatPence(ev.amountPence, ev.currency ?? "gbp") : ""}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
